<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  analyzer: { type: Object, default: null },
  availableCriteria: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const TRIGGERS = ['manual', 'on_resolve'];

const dialogRef = ref(null);
const isSaving = ref(false);
const errorMessage = ref('');

const form = ref({
  name: '',
  description: '',
  instructions: '',
  triggerType: 'manual',
  minMessages: 4,
  budgetCap: '',
  active: true,
});

// criterionId -> weight, for the ones this analyzer applies.
const selectedCriteria = ref({});

const isEditing = computed(() => Boolean(props.analyzer?.id));

const triggerOptions = computed(() =>
  TRIGGERS.map(trigger => ({
    value: trigger,
    label: t(`ANALYSIS.ANALYZERS.TRIGGER.${trigger.toUpperCase()}`),
  }))
);

const isValid = computed(
  () => form.value.name.trim() && Object.keys(selectedCriteria.value).length > 0
);

const resetForm = () => {
  const source = props.analyzer;
  form.value = {
    name: source?.name ?? '',
    description: source?.description ?? '',
    instructions: source?.instructions ?? '',
    triggerType: source?.trigger_type ?? 'manual',
    minMessages: source?.min_messages ?? 4,
    budgetCap: source?.budget_cap_credits ?? '',
    active: source?.active ?? true,
  };
  selectedCriteria.value = (source?.criteria ?? []).reduce(
    (acc, link) => ({ ...acc, [link.criterion_id]: link.weight }),
    {}
  );
  errorMessage.value = '';
};

watch(() => props.analyzer, resetForm, { immediate: true });

const isSelected = id => Object.hasOwn(selectedCriteria.value, id);

const toggleCriterion = id => {
  const next = { ...selectedCriteria.value };
  if (Object.hasOwn(next, id)) {
    delete next[id];
  } else {
    next[id] = 1;
  }
  selectedCriteria.value = next;
};

const setWeight = (id, value) => {
  selectedCriteria.value = {
    ...selectedCriteria.value,
    [id]: Math.max(1, Number(value) || 1),
  };
};

const payload = computed(() => ({
  name: form.value.name.trim(),
  description: form.value.description.trim(),
  instructions: form.value.instructions.trim(),
  trigger_type: form.value.triggerType,
  min_messages: Number(form.value.minMessages) || 0,
  budget_cap_credits:
    form.value.budgetCap === '' ? null : Number(form.value.budgetCap),
  active: form.value.active,
  criteria: Object.entries(selectedCriteria.value).map(([id, weight]) => ({
    criterion_id: Number(id),
    weight,
  })),
}));

const open = () => {
  resetForm();
  dialogRef.value.open();
};

const close = () => dialogRef.value.close();

const onConfirm = () => {
  if (!isValid.value) return;
  isSaving.value = true;
  try {
    emit('saved', payload.value);
  } finally {
    isSaving.value = false;
  }
};

const setError = message => {
  errorMessage.value = message;
};

defineExpose({ open, close, setError });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="
      isEditing
        ? t('ANALYSIS.ANALYZERS.EDIT.TITLE')
        : t('ANALYSIS.ANALYZERS.CREATE.TITLE')
    "
    :disable-confirm-button="!isValid || isSaving"
    :is-loading="isSaving"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('ANALYSIS.ANALYZERS.FORM.NAME')"
        :placeholder="t('ANALYSIS.ANALYZERS.FORM.NAME_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <TextArea
          v-model="form.instructions"
          :label="t('ANALYSIS.ANALYZERS.FORM.INSTRUCTIONS')"
          :placeholder="t('ANALYSIS.ANALYZERS.FORM.INSTRUCTIONS_PLACEHOLDER')"
          :max-length="4000"
          auto-height
        />
        <p class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.ANALYZERS.FORM.INSTRUCTIONS_HINT') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.ANALYZERS.FORM.TRIGGER') }}
        </label>
        <Select v-model="form.triggerType" :options="triggerOptions" />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <Input
            v-model="form.minMessages"
            type="number"
            :label="t('ANALYSIS.ANALYZERS.FORM.MIN_MESSAGES')"
          />
          <p class="text-sm text-n-slate-11">
            {{ t('ANALYSIS.ANALYZERS.FORM.MIN_MESSAGES_HINT') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <Input
            v-model="form.budgetCap"
            type="number"
            :label="t('ANALYSIS.ANALYZERS.FORM.BUDGET_CAP')"
            :placeholder="t('ANALYSIS.ANALYZERS.FORM.BUDGET_CAP_PLACEHOLDER')"
          />
          <p class="text-sm text-n-slate-11">
            {{ t('ANALYSIS.ANALYZERS.FORM.BUDGET_CAP_HINT') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.ANALYZERS.FORM.CRITERIA') }}
        </label>
        <p class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.ANALYZERS.FORM.CRITERIA_HINT') }}
        </p>
        <p v-if="!availableCriteria.length" class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.ANALYZERS.FORM.NO_CRITERIA') }}
        </p>
        <div
          v-for="criterion in availableCriteria"
          :key="criterion.id"
          class="flex items-center gap-3 py-1"
        >
          <Checkbox
            :model-value="isSelected(criterion.id)"
            @update:model-value="toggleCriterion(criterion.id)"
          />
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm text-n-slate-12 truncate">
              {{ criterion.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t(`ANALYSIS.CRITERIA.KIND.${criterion.kind.toUpperCase()}`) }}
            </span>
          </div>
          <Input
            v-if="isSelected(criterion.id) && criterion.kind === 'score'"
            :model-value="selectedCriteria[criterion.id]"
            type="number"
            size="sm"
            class="w-20"
            :aria-label="t('ANALYSIS.ANALYZERS.FORM.WEIGHT')"
            @update:model-value="value => setWeight(criterion.id, value)"
          />
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Switch v-model="form.active" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.ANALYZERS.FORM.ACTIVE') }}
        </span>
      </div>

      <p v-if="errorMessage" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
    </div>
  </Dialog>
</template>
